import type { Role } from "@/shared/schema/user.ts";
import type { BootstrapPayload } from "./bootContract.ts";

type RequiredAction = BootstrapPayload["required_action"];

// Where the user goes to clear each pending action.
const targets: Record<NonNullable<RequiredAction>, string> = {
  reset_password: "/force_reset_password",
  select_office: "/select/office",
  privacy_notice: "/privacy-notice-agreement",
};

// The next two lists overlap and read alike, but they answer opposite
// questions: one is about where a gate may send the user away from, the other
// about where a finished flow may send them back to.

// Leave these alone: they are where the user goes to satisfy a gate. Bounce
// someone off /login and they can never log in.
const authFlowPages = [
  "/login",
  "/logout",
  "/forgotpassword",
  "/resetpassword",
  "/unblock",
  "/auth",
];

// Never return the user here. Agreeing to the privacy notice should land them
// on the page they were reading, not back on /login, the office picker they
// just used, or a bare API endpoint.
const noReturnTo = [
  "/resetpassword/",
  "/login",
  "/agree_to_terms",
  "/select/office",
  "/api/v",
  "/pusher/auth",
  "/auth/token",
];

function matchesPage(pages: string[], pathname: string): boolean {
  return pages.some(
    (page) => pathname === page || pathname.startsWith(`${page}/`),
  );
}

function isAuthFlowPage(pathname: string): boolean {
  return matchesPage(authFlowPages, pathname);
}

// The action pages carry the pending action in their own payload, so a gate
// that fired here would bounce the user off the page that clears it.
function isRequiredActionPage(pathname: string): boolean {
  return matchesPage(Object.values(targets), pathname);
}

type BootLocation = Pick<Location, "pathname" | "search" | "hash">;

export function bootRedirectTarget(
  bootstrap: Pick<
    BootstrapPayload,
    "logged_in" | "required_action" | "otp_pending"
  >,
  { pathname, search, hash }: BootLocation,
): string | null {
  // The server stashes the destination in the session before its own
  // redirects; a client-side redirect has to hand it over explicitly, and the
  // login page validates it before storing.
  const returnTo = encodeURIComponent(pathname + search + hash);

  if (!bootstrap.logged_in) {
    return isAuthFlowPage(pathname) ? null : `/login?return_to=${returnTo}`;
  }

  // Exempting the auth flows here is broader than the server, which does
  // redirect a logged-in user with a pending action off /login and the SSO
  // pages. Harmless while those are still server-rendered; when /login moves
  // to a shell this must narrow, or such a user gets a login page instead of
  // the gate.
  if (
    bootstrap.required_action &&
    !isAuthFlowPage(pathname) &&
    !isRequiredActionPage(pathname)
  ) {
    return targets[bootstrap.required_action];
  }

  // An admin authenticated by password but not yet by OTP must not get the
  // app. After the required-action check, because that is the order the server
  // applies the two gates in.
  if (bootstrap.otp_pending && !isAuthFlowPage(pathname)) {
    return `/login/otp?return_to=${returnTo}`;
  }

  return null;
}

const RETURN_TO_KEY = "cvp-return-to";

// A server-rendered page applies these gates before any HTML reaches the
// browser, so this only ever fires on statically-served shells.
export function enforceBootRedirect(bootstrap: BootstrapPayload): boolean {
  const target = bootRedirectTarget(bootstrap, window.location);

  if (!target) {
    return false;
  }

  // Only the privacy notice returns the user to where they were; the other
  // actions end elsewhere (logged out, or on /home).
  if (target === targets.privacy_notice) {
    const { pathname, search, hash } = window.location;

    try {
      sessionStorage.setItem(RETURN_TO_KEY, pathname + search + hash);
    } catch {
      // Without storage the user lands on "/" after agreeing.
    }
  }

  // replace, like the server's 302: back must not land on a page that
  // immediately redirects here again.
  window.location.replace(target);

  return true;
}

// Anyone can edit sessionStorage, so a stored destination is untrusted input:
// check it on the way out, not just on the way in. Stricter than the server's
// own validation, which has to allow /login/[a-z] for its own flows - we only
// ever store a page the user was actually on.
function isValidReturnTo(value: string): boolean {
  if (!value.startsWith("/") || value.startsWith("//")) {
    return false;
  }

  return noReturnTo.every((rejected) => !value.includes(rejected));
}

export function consumeReturnTo(): string | null {
  let value: string | null;

  try {
    value = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    return null;
  }

  return value && isValidReturnTo(value) ? value : null;
}

// Must stay in sync with the backend's definition of an internal user.
const internalRoles: ReadonlySet<Role> = new Set([
  "limited_access",
  "consultant",
  "countrymanager",
  "referencemanager",
  "internationalmanager",
]);

export function internalUserRedirectTarget(
  bootstrap: Pick<BootstrapPayload, "current_user_roles">,
): string | null {
  const roles = bootstrap.current_user_roles;

  // No user to judge; bootRedirectTarget has already sent them to /login.
  if (!roles) {
    return null;
  }

  return roles.some((role) => internalRoles.has(role)) ? null : "/home";
}

export function enforceInternalUser(bootstrap: BootstrapPayload): boolean {
  const target = internalUserRedirectTarget(bootstrap);

  if (!target) {
    return false;
  }

  window.location.replace(target);

  return true;
}
